import React from 'react';
import {
  Switch,
  Route,
} from 'react-router-dom';

import { makeStyles, createMuiTheme, ThemeProvider } from '@material-ui/core/styles';
import Container from '@material-ui/core/Container';

import firebase from '@/services/firebase';
import useFirebaseDatabaseQuery from '@/hooks/useFirebaseDatabaseQuery';
import { useFridgeContext } from './contexts/useFridgeContext';
import { useActivityContext } from '../activity/contexts/useActivityContext';
import useAuth from './hooks/useAuth';
import withContextProviders from './contexts/index';

import defaultTheme from './defaultTheme';
import GuardedRoute from './containers/GuardedRoute';
import PersonalPage from './containers/PersonalPage';
import PreOrderPage from './containers/PreOrderPage';
import ShoppingCartPage from './containers/ShoppingCartPage';
import CheckoutPage from './containers/CheckoutPage';
import RedeemPage from './containers/RedeemPage';
import NotFoundPage from './containers/NotFoundPage';
import withTapPay from '../hocs/withTapPay';

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.common.white,
    padding: 0,
    paddingBottom: 20,
    width: '100%',
    '& .carousel.box': { height: 'calc(100vw / 2)' },
    '& .preorder-detail .carousel.box': { height: 'calc(100vw)' },
    '@media (min-width: 796px)': {
      '& .carousel.box, & .preorder-detail .carousel.box': { height: 'calc(768px / 2)' },
      maxWidth: '768px',
    },
    '@media (min-width: 976px)': {
      '& .carousel.box, & .preorder-detail .carousel.box': { height: 'calc(876px / 2)' },
      maxWidth: '876px',
    },
    '@media (min-width: 1180px)': {
      '& .carousel.box, & .preorder-detail .carousel.box': { height: 'calc(1080px / 2)' },
      maxWidth: '1080px',
    },
    '@media (min-width: 1440px)': {
      '& .carousel.box, & .preorder-detail .carousel.box': { height: 'calc(1340px / 2)' },
      maxWidth: '1340px',
    },
  },
  logo: { padding: theme.spacing(2) },
  toolbar: { flexWrap: 'wrap' },
  navTabs: { flexGrow: 1 },
  link: { margin: theme.spacing(1, 1.5) },
  content: {
    padding: 0,
    minHeight: 'calc(100vh - 100px)',
  },
}));

const App = () => {
  const classes = useStyles();

  const [checkAuthenticated] = useAuth();

  const { setFridgeDictionary } = useFridgeContext();
  const { isLoading: activityIsLoading, loadActivities } = useActivityContext();
  const dataRef = firebase.database().ref(`/${process.env.REACT_APP_MODE}/pre-order/fridges`);
  const { isLoading, data: fridges } = useFirebaseDatabaseQuery(dataRef);
  React.useEffect(() => {
    if (!isLoading && fridges) {
      setFridgeDictionary(fridges);
    }
    if (!activityIsLoading) {
      loadActivities();
    }
  }, [isLoading, fridges]);// eslint-disable-line

  const theme = createMuiTheme(defaultTheme);

  return (
    <ThemeProvider theme={theme}>
      <Container className={classes.root} maxWidth={false}>
        <Container
          component="section"
          className={classes.content}
          maxWidth={false}
        >
          <Switch>
            <Route path="/pre-orders/shopping-cart" component={ShoppingCartPage} />
            <GuardedRoute
              path="/pre-orders/personal"
              checkAuthenticated={checkAuthenticated}
              component={PersonalPage}
              redirectPath="/register"
            />
            <GuardedRoute
              path="/pre-orders/checkout"
              checkAuthenticated={checkAuthenticated}
              component={CheckoutPage}
              redirectPath="/register"
            />
            <GuardedRoute
              path="/pre-orders/redeem"
              checkAuthenticated={checkAuthenticated}
              component={RedeemPage}
              redirectPath="/auth/line/login"
            />
            <Route path="/pre-orders" component={PreOrderPage} />
            <Route path="*">
              <NotFoundPage />
            </Route>
          </Switch>
        </Container>
      </Container>
    </ThemeProvider>
  );
};

export default withContextProviders(withTapPay(App));
